/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import _ from "lodash";
import { useEffect, useState } from "react";

/**
 * Shared hook for FileManager logic that works on both web and mobile.
 * Contains: breadcrumbs, search debouncing, file filtering/sorting, path column state.
 *
 * Web-specific logic (SlickGrid, context menus, drag-drop) remains in useFileManagerFiles.
 */

interface UseFileManagerSharedProps {
  setSelectedFolder: Function;
  fileManagerData?: any;
  tempFolder?: any;
  handleSearchFiles?: Function;
  selectedFolder?: any;
  preSearchText?: string;
  onFileLoad: Function;
}

function useFileManagerShared({
  setSelectedFolder,
  fileManagerData = {},
  tempFolder = {},
  handleSearchFiles = () => {},
  selectedFolder = {},
  preSearchText = "",
  onFileLoad = () => {},
}: UseFileManagerSharedProps) {
  // Search state
  const [debounced, setDebounced] = useState<string | null>(null);
  const [query, setQuery] = useState<string | null>(preSearchText || null);
  const delay = 300;

  // UI state
  const [showPathColumn, setShowPathColumn] = useState(false);
  const [breadCrumbData, setBreadCrumbData] = useState<any[]>([]);
  const [filesData, setFilesData] = useState<any[]>([]);
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(query), delay);
    return () => clearTimeout(handler);
  }, [query]);

  // Handle pre-populated search text
  useEffect(() => {
    if (preSearchText) {
      setQuery(preSearchText);
    }
  }, [preSearchText]);

  // Call API search when debounced value changes
  useEffect(() => {
    if (debounced === null) {
      return;
    }
    if (debounced === "") {
      setShowPathColumn(false);
    } else {
      setShowPathColumn(true);
    }
    handleSearchFiles(debounced);
  }, [debounced]);

  // Clear search when folder changes
  useEffect(() => {
    if (!isFirstRender) {
      setDebounced(null);
      setQuery(null);
    }
    setIsFirstRender(false);
  }, [selectedFolder?.id]);

  // Filter and sort files based on current folder and search query
  useEffect(() => {
    const data = fileManagerData.filteredFiles || fileManagerData.files || [];
    let filteredItems = [];

    if (tempFolder.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      filteredItems = _.cloneDeep(data).filter(
        (item: any) =>
          (item.transmittalVersionId === tempFolder.id && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL") || query,
      );
    } else if (!tempFolder.resourceType) {
      filteredItems = _.cloneDeep(data).filter(
        (item: any) => (item.parentId === tempFolder.id && item.resourceType === "RESOURCE_TYPE_TRANSMITTAL") || query,
      );
    } else {
      filteredItems = _.cloneDeep(data).filter((item: any) => item.parentId === tempFolder.id || query);
    }

    // Sort: folders first (alphabetically), then files (alphabetically)
    const folders = filteredItems.filter((resource: any) => resource.resourceType === "RESOURCE_TYPE_FOLDER");
    const resourceOtherThanFolders = filteredItems.filter(
      (resource: any) => resource.resourceType !== "RESOURCE_TYPE_FOLDER",
    );
    const sortedFolders = folders.sort((a: any, b: any) => a.name?.localeCompare(b.name));
    const sortedResources = resourceOtherThanFolders.sort((a: any, b: any) => a.name?.localeCompare(b.name));
    const allFiles = [...sortedFolders, ...sortedResources];
    onFileLoad(allFiles);
    setFilesData(allFiles);
  }, [tempFolder?.id, fileManagerData]);

  // Compute breadcrumbs from tempFolder's parentIdArray
  useEffect(() => {
    const hierarchy: any[] = [];
    if (tempFolder.id && tempFolder.parentIdArray?.length) {
      tempFolder.parentIdArray.forEach((id: any) => {
        const obj = fileManagerData.files?.find((item: any) => item.id === id);
        if (obj) {
          hierarchy.push(obj);
        }
      });
    }
    if (tempFolder.id !== "0") {
      hierarchy.push(tempFolder);
    }
    const rootFolder = {
      ...fileManagerData.cwd,
      hasChild: true,
      parentId: null,
      id: "0",
    };
    setBreadCrumbData(hierarchy.length ? [rootFolder, ...hierarchy] : [rootFolder]);
  }, [tempFolder?.id, fileManagerData.cwd]);

  // Navigate to a breadcrumb folder by clicking
  const navigateToBreadcrumb = (folder: any) => {
    setSelectedFolder(folder);
  };

  // Navigate back to parent folder
  const navigateBack = () => {
    if (breadCrumbData.length > 1) {
      const parentFolder = breadCrumbData[breadCrumbData.length - 2];
      setSelectedFolder(parentFolder);
    }
  };

  return {
    // Search state
    query,
    setQuery,
    debounced,
    setDebounced,
    showPathColumn,
    setShowPathColumn,

    // Files data
    filesData,
    setFilesData,

    // Breadcrumbs
    breadCrumbData,
    setBreadCrumbData,

    // Navigation helpers
    navigateToBreadcrumb,
    navigateBack,

    // Internal state
    isFirstRender,
    setIsFirstRender,
  };
}

export default useFileManagerShared;
